import React, { useState, useEffect } from 'react';
import {
  activateImpersonationTab,
  deactivateImpersonationTab,
  getRealLocalStorageItem,
  nativeFetch,
} from '../utils/impersonationTabStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Timestamp (ms) d'expiration d'un JWT, 0 si illisible. */
const tokenExpMs = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (payload.exp || 0) * 1000;
  } catch (_) {
    return 0;
  }
};

const ImpersonationBanner = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    // Check if we're receiving impersonation data from URL (new tab scenario)
    const urlParams = new URLSearchParams(window.location.search);
    const sessionKey = urlParams.get('impersonation_session');
    
    if (sessionKey) {
      // Get impersonation data from sessionStorage (set by parent tab)
      const sessionData = sessionStorage.getItem(sessionKey);
      if (sessionData) {
        try {
          const data = JSON.parse(sessionData);
          
          // Session d'impersonation ISOLÉE à cet onglet (sessionStorage) :
          // le localStorage partagé (session Super Admin) reste intact.
          activateImpersonationTab(data);
          
          // Clean up the session key from sessionStorage
          sessionStorage.removeItem(sessionKey);
          
          // Remove the query param from URL without reload
          window.history.replaceState({}, document.title, '/');
          
          // Force reload to apply the new token
          window.location.reload();
        } catch (e) {
          console.error('Error parsing impersonation session:', e);
        }
      }
    }

    // Check impersonation status on mount and periodically
    const checkImpersonation = () => {
      const impersonating = localStorage.getItem('impersonating') === 'true';
      setIsImpersonating(impersonating);
      
      if (impersonating) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          setUserName(`${user.prenom || ''} ${user.nom || ''}`.trim() || 'Utilisateur');
        } catch (e) {
          setUserName('Utilisateur');
        }
      }
    };

    checkImpersonation();
    
    // Check every 500ms for changes
    const interval = setInterval(checkImpersonation, 500);
    
    return () => clearInterval(interval);
  }, []);

  // ── Renouvellement automatique du token d'impersonation (TTL 1 h) ────────
  // Le token d'impersonation expire au bout d'1 h (sécurité). Avant : échec
  // silencieux (« Impossible de charger les biens »). Maintenant : 2 min avant
  // l'expiration, on ré-impersonne silencieusement avec le token Super Admin
  // (toujours intact dans le localStorage réel). Si impossible → retour propre
  // au Super Admin.
  useEffect(() => {
    if (!isImpersonating) return undefined;
    let timer = null;
    let cancelled = false;

    const endSession = () => {
      deactivateImpersonationTab();
      window.location.replace('/super-admin');
    };

    const schedule = () => {
      if (cancelled) return;
      const tok = sessionStorage.getItem('token');
      if (!tok) return;
      const msBeforeRenew = tokenExpMs(tok) - Date.now() - 2 * 60 * 1000;
      timer = setTimeout(renew, Math.max(5000, msBeforeRenew));
    };

    const renew = async () => {
      if (cancelled) return;
      const saToken = getRealLocalStorageItem('token');
      let userId = null;
      try {
        userId = JSON.parse(sessionStorage.getItem('user') || '{}').id;
      } catch (_) { /* noop */ }
      if (!saToken || !userId) {
        endSession();
        return;
      }
      try {
        const r = await nativeFetch(`${API_URL}/api/super-admin/impersonate/${userId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${saToken}` },
        });
        if (!r.ok) throw new Error(`renew failed: ${r.status}`);
        const data = await r.json();
        sessionStorage.setItem('token', data.token);
        schedule(); // replanifie pour le nouveau token
      } catch (_) {
        endSession();
      }
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isImpersonating]);

  const handleReturnToSuperAdmin = () => {
    // Sortie du mode impersonation : on retire l'isolation de l'onglet,
    // le localStorage réel (session Super Admin intacte) redevient visible.
    deactivateImpersonationTab();
    window.location.href = '/super-admin';
  };
  
  const handleCloseTab = () => {
    // Nettoie uniquement la session isolée de CET onglet
    // (ne touche jamais à la session Super Admin des autres onglets).
    deactivateImpersonationTab();
    
    // Try to close the tab
    window.close();
    
    // If window.close() doesn't work (not opened by script), redirect to super admin
    setTimeout(() => {
      window.location.href = '/super-admin';
    }, 100);
  };

  if (!isImpersonating) return null;

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 md:px-6 py-2 flex items-center justify-between shadow-md z-[100] sticky top-0">
      <div className="flex items-center gap-2 md:gap-3">
        <i className="bi bi-incognito text-lg md:text-xl"></i>
        <span className="font-medium text-sm md:text-base">
          <span className="hidden sm:inline">Mode Super Admin - Vous consultez le CRM en tant que </span>
          <span className="sm:hidden">Mode Admin: </span>
          <strong>{userName}</strong>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button 
          onClick={handleReturnToSuperAdmin}
          className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition font-medium text-sm"
          title="Retourner à l'interface Super Admin"
        >
          <i className="bi bi-arrow-return-left"></i>
          <span className="hidden sm:inline">Super Admin</span>
        </button>
        <button 
          onClick={handleCloseTab}
          className="flex items-center gap-1 px-3 py-1.5 bg-red-600/30 hover:bg-red-600/50 rounded-lg transition font-medium text-sm"
          title="Fermer cet onglet"
        >
          <i className="bi bi-x-lg"></i>
          <span className="hidden md:inline">Fermer</span>
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
